import { useImperativeEffect } from '@snapchat/core-browser';
import { lazyComponent } from '@snapchat/mw-common/client';
import { type FC, useState } from 'react';

import { SuspenseWrapper } from '../SuspenseWrapper';
import {
  eventPlayerWrapperCss,
  unloadedEventPlayerPlayButtonCss,
  unloadedEventPlayerPosterCss,
} from './EventPlayer.styles';
import { EventPlayerLoadingSpinner } from './EventPlayerLoadingSpinner';
import type { LazyEventPlayerProps } from './LazyEventPlayer';

const LazyEventPlayer = lazyComponent(async () =>
  import('./LazyEventPlayer').then(module => ({ default: module.LazyEventPlayer }))
);

type EventPlayerProps = LazyEventPlayerProps & {
  /** If true, the video will automatically start playing when the component is rendered. */
  autoPlayOnRender?: boolean;
};

/** Wrapper which Lazy loads the EventPlayer component */
export const EventPlayer: FC<EventPlayerProps> = props => {
  const [hasPressedPlay, setHasPressedPlay] = useState(false);

  useImperativeEffect(() => {
    props.autoPlayOnRender && setHasPressedPlay(true);
  }, [props.autoPlayOnRender]);

  return (
    <div
      className={eventPlayerWrapperCss}
      onClick={() => setHasPressedPlay(true)}
      data-testid="event-player-wrapper"
    >
      {hasPressedPlay ? (
        <SuspenseWrapper fallbackElement={<EventPlayerLoadingSpinner />}>
          <LazyEventPlayer {...props} />
        </SuspenseWrapper>
      ) : (
        <>
          <img className={unloadedEventPlayerPosterCss} src={props.media.poster} />
          <img className={unloadedEventPlayerPlayButtonCss} src={'/play.svg'} />
        </>
      )}
    </div>
  );
};
